import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Plus, Search, Trash2, Loader2, Lock, Globe, Users, Tag as TagIcon, FolderCog, Sparkles, Eye, CheckCircle2, ShieldCheck, Zap, Pencil, Clock, ClipboardCheck } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import {
  aiBlocksToDocumentBlocks,
  deleteDocumentTemplate,
  deleteDocumentProcedure,
  listDocumentCategories,
  listDocumentProcedures,
  listDocumentSubmissions,
  listDocumentTemplates,
  setDocumentProcedureActive,
  PROCEDURE_TRIGGER_EVENTS,
  type DocumentBlock,
  type DocumentCategory,
  type DocumentProcedure,
  type DocumentSubmission,
  type DocumentTemplate,
} from '../lib/documents';
import DocumentBuilder from '../components/documents/DocumentBuilder';
import CategoryManagerModal from '../components/documents/CategoryManagerModal';
import AICreateModal from '../components/documents/AICreateModal';
import DocumentPreviewModal from '../components/documents/DocumentPreviewModal';
import SubmissionViewerModal from '../components/documents/SubmissionViewerModal';
import ProcedureBuilderModal from '../components/documents/ProcedureBuilderModal';

const TRIGGER_LABELS: Record<string, string> = Object.fromEntries(PROCEDURE_TRIGGER_EVENTS.map((e) => [e.id, e.label]));

const ACCESS_META: Record<string, { label: string; icon: any; cls: string }> = {
  admin_only: { label: 'Admins only', icon: Lock, cls: 'text-gray-600 bg-gray-100' },
  public: { label: 'Public', icon: Globe, cls: 'text-green-700 bg-green-50' },
  restricted: { label: 'Assigned', icon: Users, cls: 'text-blue-700 bg-blue-50' },
};

interface ActiveResource { id: string; first_name: string | null; last_name: string | null; avatar_url: string | null; email: string | null }

const pad2 = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const localDayKey = (iso: string) => ymd(new Date(iso));
const startOfTodayISO = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };
const toIsoStart = (s: string) => new Date(`${s}T00:00:00`).toISOString();
const toIsoEnd = (s: string) => new Date(`${s}T23:59:59.999`).toISOString();
const defaultMonthRange = () => {
  const n = new Date();
  return { from: ymd(new Date(n.getFullYear(), n.getMonth(), 1)), to: ymd(new Date(n.getFullYear(), n.getMonth() + 1, 0)) };
};

type RangePresetId = 'week' | 'lastWeek' | 'month' | 'lastMonth' | 'last7' | 'last30' | 'last90' | 'year' | 'all' | 'custom';
const RANGE_PRESETS: { id: RangePresetId; label: string }[] = [
  { id: 'week', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom range…' },
];
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const computePresetRange = (preset: RangePresetId): { from: string; to: string } => {
  const n = new Date();
  switch (preset) {
    case 'week': { const start = addDays(n, -n.getDay()); return { from: ymd(start), to: ymd(addDays(start, 6)) }; }
    case 'lastWeek': { const start = addDays(n, -n.getDay() - 7); return { from: ymd(start), to: ymd(addDays(start, 6)) }; }
    case 'month': return defaultMonthRange();
    case 'lastMonth': return { from: ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), to: ymd(new Date(n.getFullYear(), n.getMonth(), 0)) };
    case 'last7': return { from: ymd(addDays(n, -6)), to: ymd(n) };
    case 'last30': return { from: ymd(addDays(n, -29)), to: ymd(n) };
    case 'last90': return { from: ymd(addDays(n, -89)), to: ymd(n) };
    case 'year': return { from: ymd(new Date(n.getFullYear(), 0, 1)), to: ymd(new Date(n.getFullYear(), 11, 31)) };
    case 'all': return { from: '1970-01-01', to: ymd(n) };
    default: return defaultMonthRange();
  }
};
const formatDayLabel = (key: string) => new Date(`${key}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const resourceName = (r: ActiveResource) => `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || r.email || 'Unknown';
const resourceInitials = (r: ActiveResource) => `${(r.first_name?.[0] ?? '').toUpperCase()}${(r.last_name?.[0] ?? '').toUpperCase()}` || '?';

export default function Documents() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all'); // 'all' | 'uncategorized' | categoryId
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [initialDraft, setInitialDraft] = useState<{ title?: string; description?: string; suggestedCategory?: string; blocks?: DocumentBlock[] } | null>(null);
  const [activeTab, setActiveTab] = useState<'templates' | 'procedures' | 'completed'>('templates');
  const [procedures, setProcedures] = useState<DocumentProcedure[]>([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);
  const [procedureBuilderOpen, setProcedureBuilderOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [viewSubmissionId, setViewSubmissionId] = useState<string | null>(null);
  const [resources, setResources] = useState<ActiveResource[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [todaySubs, setTodaySubs] = useState<DocumentSubmission[]>([]);
  const [rangeSubs, setRangeSubs] = useState<DocumentSubmission[]>([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [range, setRange] = useState<{ from: string; to: string }>(() => defaultMonthRange());
  const [rangePreset, setRangePreset] = useState<RangePresetId>('month');

  const loadCompleted = useCallback(async () => {
    if (!businessId) return;
    setCompletedLoading(true);
    try {
      const { data: res } = await supabase
        .from('resources')
        .select('id, first_name, last_name, avatar_url, email')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('first_name', { ascending: true });
      setResources((res as ActiveResource[]) || []);
      setTodaySubs(await listDocumentSubmissions(businessId, { from: startOfTodayISO() }));
    } catch (e) { console.error('Failed to load completed documents:', e); }
    finally { setCompletedLoading(false); }
  }, [businessId]);

  const loadRange = useCallback(async () => {
    if (!businessId) return;
    setRangeLoading(true);
    try { setRangeSubs(await listDocumentSubmissions(businessId, { from: toIsoStart(range.from), to: toIsoEnd(range.to) })); }
    catch (e) { console.error('Failed to load range:', e); }
    finally { setRangeLoading(false); }
  }, [businessId, range.from, range.to]);

  useEffect(() => { if (activeTab === 'completed') loadCompleted(); }, [activeTab, loadCompleted]);
  useEffect(() => { if (activeTab === 'completed') loadRange(); }, [activeTab, loadRange]);

  const loadProcedures = useCallback(async () => {
    if (!businessId) return;
    setProceduresLoading(true);
    try { setProcedures(await listDocumentProcedures(businessId)); }
    catch (e) { console.error('Failed to load procedures:', e); }
    finally { setProceduresLoading(false); }
  }, [businessId]);

  useEffect(() => { if (activeTab === 'procedures') loadProcedures(); }, [activeTab, loadProcedures]);

  // Procedures are grouped per document — a "flow" of one or more steps.
  const procedureGroups = useMemo(() => {
    const byDoc = new Map<string, { templateId: string; title: string; procedures: DocumentProcedure[] }>();
    for (const p of procedures) {
      const key = p.template_id || 'none';
      if (!byDoc.has(key)) byDoc.set(key, { templateId: p.template_id || '', title: p.template_title || 'Untitled doc', procedures: [] });
      byDoc.get(key)!.procedures.push(p);
    }
    return Array.from(byDoc.values());
  }, [procedures]);

  const toggleProcedureGroup = async (templateId: string, makeActive: boolean) => {
    const ids = procedures.filter((p) => p.template_id === templateId).map((p) => p.id);
    setProcedures((prev) => prev.map((x) => (x.template_id === templateId ? { ...x, is_active: makeActive } : x)));
    try { await Promise.all(ids.map((id) => setDocumentProcedureActive(id, makeActive))); }
    catch (e) { console.error(e); loadProcedures(); }
  };

  const handleDeleteProcedureGroup = async (templateId: string, title: string) => {
    if (!confirm(`Delete all procedures for "${title}"?`)) return;
    const ids = procedures.filter((p) => p.template_id === templateId).map((p) => p.id);
    try { await Promise.all(ids.map((id) => deleteDocumentProcedure(id))); setProcedures((prev) => prev.filter((p) => p.template_id !== templateId)); }
    catch (e) { console.error(e); alert('Failed to delete procedures.'); }
  };

  const procedureTargetSummary = (p: DocumentProcedure): string => {
    if (p.targets.some((t) => t.target_type === 'everyone')) return 'Everyone';
    const hasOwner = p.targets.some((t) => t.target_type === 'owner');
    const roles = p.targets.filter((t) => t.target_type === 'role').length;
    const people = p.targets.filter((t) => t.target_type === 'resource').length;
    const parts: string[] = [];
    if (hasOwner) parts.push('Admin (owner)');
    if (roles) parts.push(`${roles} role${roles > 1 ? 's' : ''}`);
    if (people) parts.push(`${people} member${people > 1 ? 's' : ''}`);
    return parts.join(' · ') || 'No one';
  };

  const onPresetChange = (preset: RangePresetId) => {
    setRangePreset(preset);
    if (preset !== 'custom') setRange(computePresetRange(preset));
  };

  const filterByResource = (list: DocumentSubmission[]) =>
    selectedResourceId ? list.filter((s) => s.submitted_by === selectedResourceId) : list;

  const renderRow = (s: DocumentSubmission) => (
    <button key={s.id} onClick={() => setViewSubmissionId(s.id)} className="w-full bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:border-blue-300 transition-colors text-left">
      <div className="p-2 rounded-lg bg-green-50"><CheckCircle2 className="h-5 w-5 text-green-600" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-gray-900 truncate">{s.title || 'Document'}</div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[12px] text-gray-500">
          <span>{s.submitted_by_name || 'Unknown'}</span>
          <span>·</span>
          <span>{s.submitted_at ? new Date(s.submitted_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}</span>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 px-1.5 py-0.5 rounded"><ShieldCheck className="h-3 w-3" /> Locked</span>
    </button>
  );

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [data, cats] = await Promise.all([
        listDocumentTemplates(businessId),
        listDocumentCategories(businessId).catch(() => []),
      ]);
      setTemplates(data);
      setCategories(cats as DocumentCategory[]);
    } catch (e) {
      console.error('Failed to load documents:', e);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const categoryById = useMemo(() => {
    const m = new Map<string, DocumentCategory>();
    categories.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [templates]);

  const openNew = () => { setEditingId(null); setInitialDraft(null); setBuilderOpen(true); };
  const openEdit = (id: string) => { setEditingId(id); setInitialDraft(null); setBuilderOpen(true); };

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await deleteDocumentTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      console.error('Failed to delete document:', e);
      alert('Failed to delete document.');
    }
  };

  const filtered = templates.filter((t) => {
    if (activeCategory === 'uncategorized' && t.category_id) return false;
    if (activeCategory !== 'all' && activeCategory !== 'uncategorized' && t.category_id !== activeCategory) return false;
    if (activeTag && !(t.tags || []).includes(activeTag)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const inTitle = t.title.toLowerCase().includes(q);
      const inTags = (t.tags || []).some((tag) => tag.toLowerCase().includes(q));
      if (!inTitle && !inTags) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)',
        }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 sm:p-2.5 rounded-xl bg-blue-50 flex-shrink-0"><FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" /></div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Documents</h1>
              <p className="text-[12px] sm:text-[13px] text-gray-500 truncate">SOPs, checklists, safety forms & more</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCategoryManagerOpen(true)} title="Categories" className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-[13px] font-medium hover:bg-gray-50">
              <FolderCog className="h-4 w-4" /> <span className="hidden sm:inline">Categories</span>
            </button>
            <button onClick={() => setAiOpen(true)} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 shadow-sm">
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">AI Create</span><span className="sm:hidden">AI</span>
            </button>
            <button onClick={openNew} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 shadow-sm">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New Document</span><span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
          {([['templates', 'Templates'], ['procedures', 'Procedures'], ['completed', 'Completed']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2.5 text-[14px] font-semibold border-b-2 -mb-px transition-colors ${activeTab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'completed' ? (
          completedLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div>
              {/* Active resources (fixed) */}
              <div className="flex items-center gap-4 overflow-x-auto pb-3 mb-1">
                <button onClick={() => setSelectedResourceId(null)} className="flex flex-col items-center gap-1.5 flex-shrink-0">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 transition ${!selectedResourceId ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}><Users className="h-5 w-5" /></div>
                  <span className={`text-[11px] font-medium ${!selectedResourceId ? 'text-blue-600' : 'text-gray-600'}`}>All</span>
                </button>
                {resources.map((r) => {
                  const sel = selectedResourceId === r.id;
                  return (
                    <button key={r.id} onClick={() => setSelectedResourceId(sel ? null : r.id)} className="flex flex-col items-center gap-1.5 flex-shrink-0">
                      {r.avatar_url ? (
                        <img src={r.avatar_url} alt={resourceName(r)} className={`h-12 w-12 rounded-full object-cover transition ${sel ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`} />
                      ) : (
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 text-[14px] font-semibold transition ${sel ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>{resourceInitials(r)}</div>
                      )}
                      <span className={`text-[11px] font-medium max-w-[64px] truncate ${sel ? 'text-blue-600' : 'text-gray-600'}`}>{r.first_name || resourceName(r)}</span>
                    </button>
                  );
                })}
                {resources.length === 0 && <div className="text-[12px] text-gray-400 py-4">No active team members</div>}
              </div>

              {selectedResourceId && (
                <div className="mb-4 text-[12px] text-gray-500">
                  Showing documents completed by <span className="font-semibold text-gray-700">{resourceName(resources.find((r) => r.id === selectedResourceId) || ({} as ActiveResource))}</span>
                  {' · '}<button onClick={() => setSelectedResourceId(null)} className="text-blue-600 hover:underline">Clear</button>
                </div>
              )}

              {/* Today (fixed) */}
              <div className="mt-2">
                <h3 className="text-[13px] font-bold text-gray-900 mb-2 flex items-center gap-2">Today <span className="text-[11px] font-medium text-gray-400">{filterByResource(todaySubs).length}</span></h3>
                {(() => {
                  const list = filterByResource(todaySubs);
                  if (list.length === 0) return <div className="text-[13px] text-gray-400 bg-white border border-gray-200 rounded-xl px-4 py-5 text-center">No documents completed today.</div>;
                  return <div className="space-y-2">{list.map(renderRow)}</div>;
                })()}
              </div>

              {/* Date range (changeable) */}
              <div className="mt-7">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-[13px] font-bold text-gray-900">By date</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={rangePreset}
                      onChange={(e) => onPresetChange(e.target.value as RangePresetId)}
                      className="text-[13px] border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    >
                      {RANGE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    {rangePreset === 'custom' && (
                      <div className="flex items-center gap-1.5">
                        <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="text-[12px] border border-gray-200 rounded-lg px-2 py-1.5" />
                        <span className="text-gray-400 text-[12px]">–</span>
                        <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="text-[12px] border border-gray-200 rounded-lg px-2 py-1.5" />
                      </div>
                    )}
                  </div>
                </div>
                {rangeLoading ? (
                  <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : (() => {
                  const todayKey = localDayKey(new Date().toISOString());
                  const list = filterByResource(rangeSubs).filter((s) => s.submitted_at && localDayKey(s.submitted_at) !== todayKey);
                  if (list.length === 0) return <div className="text-[13px] text-gray-400 bg-white border border-gray-200 rounded-xl px-4 py-6 text-center">No documents in this range.</div>;
                  const groups: { key: string; items: DocumentSubmission[] }[] = [];
                  const idx: Record<string, number> = {};
                  for (const s of list) {
                    const k = localDayKey(s.submitted_at!);
                    if (idx[k] === undefined) { idx[k] = groups.length; groups.push({ key: k, items: [] }); }
                    groups[idx[k]].items.push(s);
                  }
                  return (
                    <div className="space-y-5">
                      {groups.map((g) => (
                        <div key={g.key}>
                          <div className="text-[12px] font-semibold text-gray-500 mb-2">{formatDayLabel(g.key)}</div>
                          <div className="space-y-2">{g.items.map(renderRow)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )
        ) : activeTab === 'procedures' ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-[13px] text-gray-500">Connect documents to events — assign to people and require them at clock in/out, breaks, and more.</p>
              <button onClick={() => { setEditingTemplateId(null); setProcedureBuilderOpen(true); }} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 shadow-sm flex-shrink-0">
                <Plus className="h-4 w-4" /> New Procedure
              </button>
            </div>
            {proceduresLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : procedureGroups.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
                <Zap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <div className="text-[15px] font-medium text-gray-600">No procedures yet</div>
                <div className="text-[13px] text-gray-400 mb-4">Create a procedure to assign documents and require them at clock events.</div>
                <button onClick={() => { setEditingTemplateId(null); setProcedureBuilderOpen(true); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700">
                  <Plus className="h-4 w-4" /> New Procedure
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {procedureGroups.map((g) => {
                  const anyActive = g.procedures.some((p) => p.is_active);
                  return (
                  <div key={g.templateId || 'none'} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:border-blue-300 transition-colors">
                    <div className={`p-2 rounded-lg ${anyActive ? 'bg-blue-50' : 'bg-gray-100'}`}><FileText className={`h-5 w-5 ${anyActive ? 'text-blue-600' : 'text-gray-400'}`} /></div>
                    <button onClick={() => { setEditingTemplateId(g.templateId); setProcedureBuilderOpen(true); }} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-gray-900 truncate">{g.title}</span>
                        {!anyActive && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Off</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[12px]">
                        {g.procedures.map((p) => (
                          p.kind === 'verification' ? (
                            <span key={p.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">
                              <ShieldCheck className="h-3 w-3" /> Sign-off · {procedureTargetSummary(p)}
                            </span>
                          ) : p.kind === 'booking' ? (
                            <span key={p.id} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${p.enforcement === 'optional' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-700'}`}>
                              <ClipboardCheck className="h-3 w-3" /> Booking · {p.facing === 'customer' ? 'customer sign-off' : 'checklist'}{p.enforcement === 'optional' ? ' · optional' : ' · required'}
                            </span>
                          ) : (
                            p.triggers.map((t, i) => (
                              <span key={`${p.id}-${i}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${t.mandatory ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                                <Clock className="h-3 w-3" />{TRIGGER_LABELS[t.event] || t.event}{t.mandatory ? ' · required' : ''}
                              </span>
                            ))
                          )
                        ))}
                      </div>
                    </button>
                    <button onClick={() => toggleProcedureGroup(g.templateId, !anyActive)} title={anyActive ? 'Turn off' : 'Turn on'} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${anyActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${anyActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                    <button onClick={() => { setEditingTemplateId(g.templateId); setProcedureBuilderOpen(true); }} title="Edit" className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => handleDeleteProcedureGroup(g.templateId, g.title)} title="Delete" className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="w-full pl-9 pr-4 py-2.5 bg-white rounded-xl text-[15px] text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            style={{ fontSize: '16px' }}
          />
        </div>

        {/* Category filter chips */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-1">
          {[{ id: 'all', name: 'All', color: '#64748b' }, ...categories, { id: 'uncategorized', name: 'Uncategorized', color: '#94a3b8' }].map((c) => {
            const active = activeCategory === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap border transition-colors ${active ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                style={active ? { backgroundColor: (c as any).color } : undefined}
              >
                {c.id !== 'all' && c.id !== 'uncategorized' && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: (c as any).color }} />}
                {c.name}
              </button>
            );
          })}
        </div>

        {/* Tag filter */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-3">
            <TagIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                className={`text-[12px] px-2 py-0.5 rounded-full whitespace-nowrap ${activeTag === tag ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <div className="text-[15px] font-medium text-gray-600">No documents yet</div>
            <div className="text-[13px] text-gray-400 mb-4">Create your first SOP, checklist, or safety form.</div>
            <button onClick={openNew} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700">
              <Plus className="h-4 w-4" /> New Document
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((t) => {
              const access = ACCESS_META[t.access_scope] || ACCESS_META.admin_only;
              const AccessIcon = access.icon;
              const cat = t.category_id ? categoryById.get(t.category_id) : null;
              return (
                <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:border-blue-300 transition-colors">
                  <button onClick={() => openEdit(t.id)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-semibold text-gray-900 truncate">{t.title}</span>
                      {t.status === 'draft' && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Draft</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {cat && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${cat.color}1a`, color: cat.color }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cat.color }} />{cat.name}</span>}
                      {(t.tags || []).map((tag) => <span key={tag} className="text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">#{tag}</span>)}
                      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${access.cls}`}><AccessIcon className="h-3 w-3" /> {access.label}</span>
                      <span className="text-[11px] text-gray-400">Updated {new Date(t.updated_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                  <button onClick={() => setPreviewId(t.id)} title="Preview" className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(t.id, t.title)} title="Delete" className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      {viewSubmissionId && (
        <SubmissionViewerModal submissionId={viewSubmissionId} onClose={() => setViewSubmissionId(null)} />
      )}

      {procedureBuilderOpen && businessId && (
        <ProcedureBuilderModal
          businessId={businessId}
          templateId={editingTemplateId}
          onClose={() => { setProcedureBuilderOpen(false); setEditingTemplateId(null); }}
          onSaved={loadProcedures}
        />
      )}

      {builderOpen && businessId && (
        <DocumentBuilder
          businessId={businessId}
          templateId={editingId}
          initialDraft={initialDraft}
          onClose={() => { setBuilderOpen(false); setInitialDraft(null); }}
          onSaved={load}
        />
      )}

      {aiOpen && businessId && (
        <AICreateModal
          businessId={businessId}
          businessName={(selectedBusiness as any)?.name}
          isOpen={aiOpen}
          onClose={() => setAiOpen(false)}
          onGenerated={(draft) => {
            setEditingId(null);
            setInitialDraft({
              title: draft.title,
              description: draft.description,
              suggestedCategory: draft.suggestedCategory,
              blocks: aiBlocksToDocumentBlocks(draft.blocks),
            });
            setBuilderOpen(true);
          }}
        />
      )}

      {categoryManagerOpen && businessId && (
        <CategoryManagerModal
          businessId={businessId}
          isOpen={categoryManagerOpen}
          onClose={() => setCategoryManagerOpen(false)}
          onChanged={load}
        />
      )}

      {previewId && (
        <DocumentPreviewModal
          templateId={previewId}
          onClose={() => setPreviewId(null)}
          onEdit={(docId) => openEdit(docId)}
        />
      )}
    </div>
  );
}
